import { Component, ViewEncapsulation, ViewChild, OnInit, inject, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';

import { Client } from 'src/providers/client/client';
import { WindowService } from 'src/shared/providers/window.service';

import { Util } from 'src/shared/util/util';

import { PaymentsConfigService } from 'src/shared/services/payments-config.service';

import { PaymentsConfigRule } from 'src/models/payments-config.model';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'payments-config-content',
  templateUrl: './payments-config-content.component.html',
  styleUrls: ['./payments-config-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PaymentsConfigContentComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private client = inject(Client);
  private paymentsConfigService = inject(PaymentsConfigService);
  private util = inject(Util);

  readonly windowService = inject(WindowService);

  rules: PaymentsConfigRule[] = [];
  headers: string[] = [];
  rows: any[] = [];
  isDataLoaded = false;
  errorMessage: string;
  @ViewChild('tidyTable') tidyTable: any;

  async ngOnInit() {
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load payments config';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    this.buildHeaders();
    try {
      this.rules = await this.paymentsConfigService.getPaymentsConfig();
    } catch {
      this.rules = [];
    }
    this.buildRows(this.rules);
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  buildHeaders(): void {
    this.headers = [
      'Property',
      'Channel',
      'Payment Method',
      'Processing Instructions',
      'Priority',
      'last-column'
    ];
  }

  buildRows(rules: PaymentsConfigRule[]): void {
    this.rows = rules.map(rule => ({
      id: rule.id,
      'Property': {
        value: rule.property_name || 'All Properties',
        action: null,
      },
      'Channel': {
        value: rule.channel || 'All Channels',
        action: null,
      },
      'Payment Method': {
        value: rule.payment_method || 'All Methods',
        action: null,
      },
      'Processing Instructions': {
        value: rule.processing_instructions || '',
        action: null,
      },
      'Priority': {
        value: String(rule.priority),
        action: null,
      },
      'last-column': {
        value: '',
        action: null,
      },
    }));
  }

  sortChanged(sort: any): void {
    this.rows.sort((a, b) => {
      const valA = a[sort.active]?.value || '';
      const valB = b[sort.active]?.value || '';

      if (sort.active === 'Priority') {
        const numA = Number(valA);
        const numB = Number(valB);
        return sort.direction === 'asc' ? numA - numB : numB - numA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) {return sort.direction === 'asc' || sort.direction === '' ? -1 : 1;}
      if (strA > strB) {return sort.direction === 'asc' || sort.direction === '' ? 1 : -1;}
      return 0;
    });
  }

  onTableColumnClick(event: any): void {
    const foundRule = this.rules.find(rule => rule.id === event.element.id);
    if (!foundRule) {return;}
    // Future: open rule detail/edit page
  }

  getEmptyStateMessage(): string {
    return 'No payment processing rules configured. Rules determine how inbound guest payments are processed. Most specific rules (by property, channel, and payment method) are applied first.';
  }
}
